import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from '@/components/ui/language-selector';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Clock, Check, Copy } from 'lucide-react';

// ── EXPIRY DATE (UTC pour éviter les décalages horaires) ──
const EXPIRY_DATE = new Date('2026-07-31T23:59:59Z');

const t = {
  en: {
    navHome: 'Home',
    badge: 'Founding Members Program',
    title: 'Be Part of the ',
    titleAccent: 'Origin Story',
    subtitle:
      'AcquizGroup is opening its doors to a restricted circle of founding members — priority access, -40% for life on your plan, and your name in the history of the platform.',
    countSuffix: ' founding members already registered',
    expiresLabel: 'Offer expires in',
    days: 'd',
    hours: 'h',
    minutes: 'm',
    seconds: 's',
    limitedSpots: 'Limited to 200 founding members',

    // Buyer pricing
    buyerPricingTitle: 'Founding Member Pricing — Buyer Plans',
    buyerPricingSubtitle:
      'Lock in -40% off launch prices forever. Applied automatically with code FOUNDING40 at checkout.',
    consultantPricingTitle: 'Founding Member Pricing — Consultant Plans',
    consultantPricingSubtitle:
      'Lock in -40% off launch prices forever. Applied automatically with code FOUNDING40 at checkout.',

    regular: 'Regular',
    launch: 'Launch',
    founding: 'Founding',
    perYear: '/yr',
    perDeal: '/deal',
    save: 'Save',
    totalSavings: 'total · 60% off regular',
    totalSavingsConsultant: 'total · 64% off regular',
    mostPopular: 'Most Popular',
    bestValue: 'Best Value',
    payPerDeal: 'Pay per deal',
    byodNote: 'FOUNDING40 does not apply to BYOD',
    couponBox: 'Use code FOUNDING40 at checkout',
    ctaFounding: 'Register as Founding Member →',
    ctaBuyer: 'Register as Buyer →',
    ctaConsultantFounding: 'Register as Founding Consultant →',
    sectionNote:
      '* Code FOUNDING40 applied automatically at checkout. Expires July 31, 2026.',

    // Benefits
    benefitsTitle: 'What you get',
    benefit1Title: 'Founding Member Badge',
    benefit1Desc:
      'A permanent badge on your profile — visible to all platform users forever.',
    benefit2Title: '-40% Extra on Launch Price — For Life',
    benefit2Desc:
      'An additional 40% off our already-discounted launch prices, applied at every renewal. Forever.',
    benefit3Title: 'Beta Access',
    benefit3Desc:
      'Access the platform before the public launch. Shape its development.',
    benefit4Title: 'Your Voice Counts',
    benefit4Desc:
      'Direct access to the founding team for feedback and feature requests.',
  },
  fr: {
    navHome: 'Accueil',
    badge: 'Programme Membres Fondateurs',
    title: "Faites partie de l'",
    titleAccent: 'Histoire',
    subtitle:
      "AcquizGroup ouvre ses portes à un cercle restreint de membres fondateurs — accès prioritaire, -40% à vie sur votre forfait, et votre nom dans l'histoire de la plateforme.",
    countSuffix: ' membres fondateurs déjà inscrits',
    expiresLabel: 'Offre disponible encore',
    days: 'j',
    hours: 'h',
    minutes: 'm',
    seconds: 's',
    limitedSpots: 'Limité à 200 membres fondateurs',

    buyerPricingTitle: 'Tarifs Membres Fondateurs — Forfaits Acheteurs',
    buyerPricingSubtitle:
      'Bloquez -40% sur les prix de lancement à vie. Appliqué automatiquement avec le code FOUNDING40 lors du paiement.',
    consultantPricingTitle: 'Tarifs Membres Fondateurs — Forfaits Consultants',
    consultantPricingSubtitle:
      'Bloquez -40% sur les prix de lancement à vie. Appliqué automatiquement avec le code FOUNDING40 lors du paiement.',

    regular: 'Régulier',
    launch: 'Lancement',
    founding: 'Fondateur',
    perYear: '/an',
    perDeal: '/deal',
    save: 'Économisez',
    totalSavings: 'total · 60% vs régulier',
    totalSavingsConsultant: 'total · 64% vs régulier',
    mostPopular: 'Le Plus Populaire',
    bestValue: 'Meilleure Valeur',
    payPerDeal: 'Paiement par deal',
    byodNote: "FOUNDING40 ne s'applique pas au BYOD",
    couponBox: 'Utilisez le code FOUNDING40 au paiement',
    ctaFounding: "S'inscrire comme Membre Fondateur →",
    ctaBuyer: "S'inscrire comme Acheteur →",
    ctaConsultantFounding: "S'inscrire comme Consultant Fondateur →",
    sectionNote:
      '* Le code FOUNDING40 est appliqué automatiquement lors du paiement. Expire le 31 juillet 2026.',

    benefitsTitle: 'Ce que vous obtenez',
    benefit1Title: 'Badge Membre Fondateur',
    benefit1Desc:
      'Un badge permanent sur votre profil — visible par tous les utilisateurs de la plateforme pour toujours.',
    benefit2Title: '-40% Supplémentaire sur le Prix de Lancement — À Vie',
    benefit2Desc:
      'Une réduction supplémentaire de 40% sur nos prix de lancement déjà réduits, appliquée à chaque renouvellement. Pour toujours.',
    benefit3Title: 'Accès Bêta',
    benefit3Desc:
      'Accédez à la plateforme avant le lancement public. Participez à son développement.',
    benefit4Title: 'Votre Voix Compte',
    benefit4Desc:
      'Direct access to the founding team for feedback and feature requests.',
  },
};

function useCountdown(target: Date) {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// ── COMPTEUR RÉEL — nombre de Buyers + Consultants inscrits (Firestore) ──
// Rafraîchi toutes les 60s pendant que la page reste ouverte.
function useRealFoundingCounter(fallback: number) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('roles', 'array-contains-any', ['buyer', 'consultant'])
        );
        const snap = await getCountFromServer(q);
        if (!cancelled) setCount(snap.data().count);
      } catch (err) {
        console.warn('[EarlyAccess] founding counter fetch failed:', err);
        // On garde la valeur précédente (ou le fallback) plutôt que de casser l'affichage
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return count ?? fallback;
}

type PlanCard = {
  name: string;
  badge?: 'popular' | 'best' | 'byod';
  regular: number;
  launch: number;
  founding?: number;
  unit: 'year' | 'deal';
  launchDiscountLabel: string;
  foundingDiscountLabel?: string;
  saveLabel?: string;
  features: string[];
  ctaLabel: string;
  ctaPath: string;
  note?: string;
};

export default function EarlyAccess() {
  const { language } = useLanguage();
  const lang = (language as 'en' | 'fr') || 'en';
  const labels = t[lang];
  const countdown = useCountdown(EXPIRY_DATE);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Note : le compteur réel (useRealFoundingCounter) reste disponible dans ce fichier
  // pour être réactivé facilement plus tard, une fois que le nombre d'inscriptions
  // réelles sera assez élevé pour être affiché. Non utilisé pour l'instant.

  const isExpired = EXPIRY_DATE.getTime() <= Date.now();

  const copyCoupon = () => {
    navigator.clipboard?.writeText('FOUNDING40');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // ── BUYER PLANS ──
  const buyerPlans: PlanCard[] = [
    {
      name: lang === 'fr' ? 'Essentiel' : 'Essential',
      regular: 1499,
      launch: 999,
      founding: 599,
      unit: 'year',
      launchDiscountLabel: '-33%',
      foundingDiscountLabel: '-40% extra',
      saveLabel: `$900${labels.perYear} ${labels.totalSavings}`,
      features:
        lang === 'fr'
          ? [
              'Accès complet au Buyer Dashboard',
              'Accès complet au Deal Monitor',
              "Recherche avancée d'entreprises",
              'Signature électronique NDA',
              "Accès aux offres des prêteurs (anonyme jusqu'à LOI)",
              'Contact direct avec vendeurs',
              'Messagerie sécurisée',
              'Documents confidentiels inclus',
              'BYOD à 300$ par deal soumis',
            ]
          : [
              'Full access to Buyer Dashboard',
              'Full access to Deal Monitor',
              'Advanced business search',
              'Electronic NDA signing',
              'Access to lender offers (anonymous until LOI)',
              'Direct contact with sellers',
              'Secure messaging',
              'Confidential documents included',
              'BYOD at $300 per deal submitted',
            ],
      ctaLabel: labels.ctaFounding,
      ctaPath: '/register/buyers?promo=FOUNDING40&plan=essential',
    },
    {
      name: lang === 'fr' ? 'Essentiel Plus' : 'Essential Plus',
      badge: 'popular',
      regular: 3299,
      launch: 2199,
      founding: 1319,
      unit: 'year',
      launchDiscountLabel: '-33%',
      foundingDiscountLabel: '-40% extra',
      saveLabel: `$1,980${labels.perYear} ${labels.totalSavings}`,
      features:
        lang === 'fr'
          ? [
              'Tous les avantages Essentiel',
              'BYOD à 300$ par deal soumis',
              "Rapport d'analyse de marché INCLUS",
              'Support prioritaire dédié',
              'Accès anticipé aux nouveaux listings',
              'Badge Elite Buyer sur profil',
              'MailBox de deal dédiée sécurisée',
              'Stockage sécurisé (Data Room)',
              'Outil de visioconférence',
              'Accès à la plateforme E-Learning',
            ]
          : [
              'All Essential benefits',
              'BYOD at $300 per deal submitted',
              'Market Analysis Report INCLUDED',
              'Dedicated priority support',
              'Early access to new listings',
              'Elite Buyer badge on profile',
              'Secure dedicated deal MailBox',
              'Secure document storage (Data Room)',
              'Video conferencing tool',
              'Access to E-Learning platform',
            ],
      ctaLabel: labels.ctaFounding,
      ctaPath: '/register/buyers?promo=FOUNDING40&plan=essentialPlus',
    },
    {
      name: lang === 'fr' ? 'Acquisition Pro' : 'Acquisition Pro',
      badge: 'best',
      regular: 5999,
      launch: 3999,
      founding: 2399,
      unit: 'year',
      launchDiscountLabel: '-33%',
      foundingDiscountLabel: '-40% extra',
      saveLabel: `$3,600${labels.perYear} ${labels.totalSavings}`,
      features:
        lang === 'fr'
          ? [
              'Tous les avantages Essentiel Plus',
              'Rapports Deal Quality illimités',
              'Rapports de faisabilité financement illimités',
              '3 études de marché incluses/an',
              '2 plans stratégiques inclus/an',
              '2 rapports comparatifs marché inclus/an',
              'Badge Acheteur Sérieux Vérifié sur profil',
              'Priorité maximale sur les demandes de contact',
              'BYOD : 199$/deal soumis',
              'Accès complet E-Learning',
              'Frais de gestion réduits : 0,75% au lieu de 1%',
            ]
          : [
              'All Essential Plus benefits',
              'Unlimited Deal Quality Reports',
              'Unlimited Financing Feasibility Reports',
              '3 Market Studies included/year',
              '2 Strategic Plans included/year',
              '2 Market Comparison Reports included/year',
              'Badge Verified Serious Buyer on profile',
              'Maximum priority on contact requests',
              'BYOD: $199/deal submitted',
              'Full E-Learning access',
              'Reduced management fee: 0.75% instead of 1%',
            ],
      ctaLabel: labels.ctaFounding,
      ctaPath: '/register/buyers?promo=FOUNDING40&plan=acquisitionPro',
    },
    {
      name: 'BYOD',
      badge: 'byod',
      regular: 720,
      launch: 499,
      unit: 'deal',
      launchDiscountLabel: '-31%',
      features:
        lang === 'fr'
          ? [
              'Accès complet au Buyer Dashboard',
              'Soumission pour analyse et pré-financement',
              'Offres des prêteurs sur votre dashboard',
              'MailBox de deal dédiée sécurisée',
              'Stockage sécurisé (Data Room)',
              'Outil de visioconférence',
              'Accès à la plateforme E-Learning',
            ]
          : [
              'Full access to Buyer Dashboard',
              'Submission for analysis and pre-financing',
              'Lender offers on your dashboard',
              'Secure dedicated deal MailBox',
              'Secure document storage (Data Room)',
              'Video conferencing tool',
              'Access to E-Learning platform',
            ],
      ctaLabel: labels.ctaBuyer,
      ctaPath: '/register/buyers?plan=byod',
      note: labels.byodNote,
    },
  ];

  // ── CONSULTANT PLANS ──
  const consultantPlans: PlanCard[] = [
    {
      name: 'Explorer',
      regular: 660,
      launch: 399,
      founding: 239,
      unit: 'year',
      launchDiscountLabel: '-40%',
      foundingDiscountLabel: '-40% extra',
      saveLabel: `$421${labels.perYear} ${labels.totalSavingsConsultant}`,
      features:
        lang === 'fr'
          ? [
              'Profil consultant public',
              "Jusqu'à 3 domaines d'expertise",
              'Messagerie client basique',
              'Accès à la plateforme E-Learning',
            ]
          : [
              'Public consultant profile',
              'Up to 3 expertise areas',
              'Basic client messaging',
              'Access to E-Learning platform',
            ],
      ctaLabel: labels.ctaConsultantFounding,
      ctaPath: '/register/consultant?promo=FOUNDING40&plan=consultant_explorer',
    },
    {
      name: 'Pro',
      regular: 1200,
      launch: 720,
      founding: 432,
      unit: 'year',
      launchDiscountLabel: '-40%',
      foundingDiscountLabel: '-40% extra',
      saveLabel: `$768${labels.perYear} ${labels.totalSavingsConsultant}`,
      features:
        lang === 'fr'
          ? [
              'Tout du forfait Explorer',
              "Domaines d'expertise illimités",
              'Messagerie client & alertes deals',
              'Badges de certification',
              'Visibilité prioritaire',
            ]
          : [
              'Everything in Explorer',
              'Unlimited expertise areas',
              'Client messaging & deal alerts',
              'Certification badges',
              'Priority visibility',
            ],
      ctaLabel: labels.ctaConsultantFounding,
      ctaPath: '/register/consultant?promo=FOUNDING40&plan=consultant_pro',
    },
    {
      name: lang === 'fr' ? 'Élite' : 'Elite',
      badge: 'best',
      regular: 2400,
      launch: 1440,
      founding: 864,
      unit: 'year',
      launchDiscountLabel: '-40%',
      foundingDiscountLabel: '-40% extra',
      saveLabel: `$1,536${labels.perYear} ${labels.totalSavingsConsultant}`,
      features:
        lang === 'fr'
          ? [
              'Tout du forfait Pro',
              'Visibilité premium',
              'Taux de commission réduit',
              'Support prioritaire',
              'Gestionnaire de compte dédié',
            ]
          : [
              'Everything in Pro',
              'Premium visibility',
              'Reduced commission rate',
              'Priority support',
              'Dedicated account manager',
            ],
      ctaLabel: labels.ctaConsultantFounding,
      ctaPath: '/register/consultant?promo=FOUNDING40&plan=consultant_elite',
    },
  ];

  const renderBadge = (badge?: PlanCard['badge']) => {
    if (!badge) return null;
    if (badge === 'popular') {
      return (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CEAB65] text-[#1D314D] text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {labels.mostPopular}
        </div>
      );
    }
    if (badge === 'best') {
      return (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1D314D] text-[#CEAB65] border border-[#CEAB65] text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {labels.bestValue}
        </div>
      );
    }
    return (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-white/10 text-white border border-white/30 text-xs font-bold px-3 py-1 rounded-full">
        {labels.payPerDeal}
      </div>
    );
  };

  const PlanCardView = ({ plan }: { plan: PlanCard }) => {
    const unitLabel = plan.unit === 'year' ? labels.perYear : labels.perDeal;
    return (
      <div
        className={`relative bg-[#1D314D] border rounded-xl p-6 flex flex-col ${
          plan.badge === 'popular'
            ? 'border-[#CEAB65]/70 shadow-[0_0_30px_-10px_rgba(206,171,101,0.5)]'
            : plan.badge === 'best'
              ? 'border-[#CEAB65]/40'
              : 'border-white/10'
        }`}
      >
        {renderBadge(plan.badge)}

        <h4 className="text-lg font-bold mb-4 mt-2">{plan.name}</h4>

        {/* Pricing block */}
        <div className="space-y-1 mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-gray-400 line-through text-sm">
              ${plan.regular.toLocaleString()}
              {unitLabel}
            </span>
            <span className="text-gray-400 text-xs uppercase">{labels.regular}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-white font-semibold">
              🚀 ${plan.launch.toLocaleString()}
              {unitLabel}
            </span>
            <span className="text-white/60 text-xs">({plan.launchDiscountLabel})</span>
          </div>
          {plan.founding !== undefined && (
            <div className="flex items-baseline gap-2 pt-1">
              <span className="text-[#CEAB65] text-3xl font-bold">
                ${plan.founding.toLocaleString()}
              </span>
              <span className="text-[#CEAB65] text-sm">{unitLabel}</span>
              {plan.foundingDiscountLabel && (
                <span className="text-[#CEAB65]/80 text-xs">
                  ({plan.foundingDiscountLabel})
                </span>
              )}
            </div>
          )}
          {plan.saveLabel && (
            <div className="inline-block bg-green-500/20 text-green-300 border border-green-500/40 rounded-full px-3 py-1 text-xs font-semibold mt-2">
              {labels.save} {plan.saveLabel}
            </div>
          )}
          {plan.note && (
            <p className="text-xs text-white/50 italic mt-2">{plan.note}</p>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-grow">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white/80">
              <Check className="w-4 h-4 text-[#CEAB65] flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* Coupon pill */}
        {plan.founding !== undefined && (
          <button
            onClick={copyCoupon}
            className="flex items-center justify-center gap-2 w-full bg-[#CEAB65]/15 border border-[#CEAB65]/50 text-[#CEAB65] rounded-full px-3 py-1.5 text-xs font-mono font-semibold mb-3 hover:bg-[#CEAB65]/25 transition-colors"
            type="button"
          >
            <Copy className="w-3 h-3" />
            {copied ? '✓ FOUNDING40' : labels.couponBox}
          </button>
        )}

        {/* CTA */}
        <Button
          onClick={() => navigate(plan.ctaPath)}
          className="w-full bg-[#CEAB65] text-[#1D314D] hover:bg-[#b8954f] font-bold py-5"
          disabled={isExpired}
        >
          {plan.ctaLabel}
        </Button>
      </div>
    );
  };

  const benefits = [
    { title: labels.benefit1Title, desc: labels.benefit1Desc },
    { title: labels.benefit2Title, desc: labels.benefit2Desc },
    { title: labels.benefit3Title, desc: labels.benefit3Desc },
    { title: labels.benefit4Title, desc: labels.benefit4Desc },
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      {/* NAV */}
      <nav className="border-b border-white/10 bg-[#0A1628]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight">
            Acquiz<span className="text-[#CEAB65]">Group</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="light" />
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            >
              <Link to="/">{labels.navHome}</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-[#CEAB65]/10 via-transparent to-[#1D314D]/40 pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#CEAB65]/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-[#CEAB65]/20 text-[#CEAB65] border border-[#CEAB65]/40 hover:bg-[#CEAB65]/20 px-3 py-1">
            <Crown className="w-3.5 h-3.5 mr-1.5" />
            {labels.badge}
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            {labels.title}
            <span className="text-[#CEAB65]">{labels.titleAccent}</span>
          </h1>

          <p className="text-lg text-white/70 mb-10 max-w-2xl mx-auto leading-relaxed">
            {labels.subtitle}
          </p>

          {/* Countdown */}
          {!isExpired && (
            <div className="mb-8">
              <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-3">
                <Clock className="w-3.5 h-3.5" />
                {labels.expiresLabel}
              </div>
              <div className="flex justify-center gap-3">
                {[
                  { val: countdown.days, label: labels.days },
                  { val: countdown.hours, label: labels.hours },
                  { val: countdown.minutes, label: labels.minutes },
                  { val: countdown.seconds, label: labels.seconds },
                ].map(({ val, label }) => (
                  <div
                    key={label}
                    className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 min-w-[64px]"
                  >
                    <div className="text-2xl font-bold text-[#CEAB65] tabular-nums">
                      {val.toString().padStart(2, '0')}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Urgency badge — no live headcount displayed, cap policy only */}
          <p className="inline-flex items-center gap-2 text-sm text-[#CEAB65] border border-[#CEAB65]/40 bg-[#CEAB65]/10 rounded-full px-4 py-1.5">
            🎖 {labels.limitedSpots}
          </p>
        </div>
      </section>

      {/* BUYER PRICING */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">
            {labels.buyerPricingTitle}
          </h2>
          <p className="text-center text-white/60 mb-12 max-w-2xl mx-auto">
            {labels.buyerPricingSubtitle}
          </p>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {buyerPlans.map((plan) => (
              <PlanCardView key={plan.name} plan={plan} />
            ))}
          </div>

          <p className="text-xs text-white/50 text-center mt-8">{labels.sectionNote}</p>
        </div>
      </section>

      {/* CONSULTANT PRICING */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">
            {labels.consultantPricingTitle}
          </h2>
          <p className="text-center text-white/60 mb-12 max-w-2xl mx-auto">
            {labels.consultantPricingSubtitle}
          </p>

          <div className="grid gap-6 md:grid-cols-3">
            {consultantPlans.map((plan) => (
              <PlanCardView key={plan.name} plan={plan} />
            ))}
          </div>

          <p className="text-xs text-white/50 text-center mt-8">{labels.sectionNote}</p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            {labels.benefitsTitle}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {benefits.map((b) => (
              <div
                key={b.title}
                className="bg-[#1D314D] border border-white/10 rounded-xl p-6"
              >
                <h3 className="text-lg font-bold text-[#CEAB65] mb-2">{b.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-8 text-center text-xs text-white/50">
        © 2026 AcquizGroup. All rights reserved.
      </footer>
    </div>
  );
}